/**
 * VegaAgentConfig — Admin
 * Configuration LLM + seuils analyseurs + CRON + mémoire de l'agent VEGA.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Radar, Save, Check, Loader, Zap, Play, Trash2, Plus, Clock, AlertTriangle, Settings } from 'lucide-react';
import {
  getVegaSettings, updateVegaSettings, testVegaConnection, runVegaAnalysis,
  getVegaMemories, addVegaMemory, deleteVegaMemory,
} from '../../api';

const PROVIDERS = [
  { value: 'claude',  label: 'Claude (Anthropic)', placeholder: 'claude-sonnet-4-6' },
  { value: 'openai',  label: 'OpenAI (GPT)',        placeholder: 'gpt-4o' },
  { value: 'mistral', label: 'Mistral AI',           placeholder: 'mistral-large-latest' },
  { value: 'gemini',  label: 'Gemini (Google)',      placeholder: 'gemini-2.0-flash' },
  { value: 'ollama',  label: 'Ollama (local)',       placeholder: 'qwen2.5:7b' },
  { value: 'cortex',  label: 'Cortex (Delogic)',     placeholder: 'ollama/qwen2.5:7b' },
];

const TONES = [
  { value: 'professionnel', label: 'Professionnel', description: 'Précis et rigoureux (défaut)' },
  { value: 'amical',        label: 'Amical',        description: 'Accessible et chaleureux' },
  { value: 'concis',        label: 'Concis',        description: 'Direct, va à l\'essentiel' },
  { value: 'neutre',        label: 'Neutre',        description: 'Factuel, sans affect particulier' },
];

const CRON_PRESETS = [
  { value: '0 */1 * * *', label: 'Toutes les heures' },
  { value: '0 */2 * * *', label: 'Toutes les 2h' },
  { value: '0 */4 * * *', label: 'Toutes les 4h (défaut)' },
  { value: '0 */6 * * *', label: 'Toutes les 6h' },
  { value: '0 */12 * * *', label: 'Toutes les 12h' },
  { value: '0 8 * * *',   label: '1×/jour (8h)' },
];

const THRESHOLDS = [
  { key: 'stockDaysThreshold',          label: 'Stocks — jours avant alerte rupture',  unit: 'jours', min: 1, max: 30 },
  { key: 'traceabilityHoursThreshold',  label: 'Traçabilité — heures avant expiration', unit: 'heures', min: 1, max: 168 },
  { key: 'maintenanceDaysThreshold',    label: 'Maintenance — jours de retard',         unit: 'jours', min: 1, max: 60 },
  { key: 'hrContractDaysThreshold',     label: 'RH — jours avant fin contrat',          unit: 'jours', min: 7, max: 90 },
  { key: 'budgetVariancePercent',       label: 'Budget — écart CA vs charges',           unit: '%', min: 5, max: 50 },
  { key: 'taskOverdueDaysThreshold',    label: 'Tâches — jours de retard',              unit: 'jours', min: 1, max: 30 },
];

const MEMORY_TYPES = ['contexte', 'habitude', 'règle', 'préférence', 'observation'];
const MEMORY_CATEGORIES = ['organisation', 'finance', 'rh', 'stocks', 'maintenance', 'crèche', 'traçabilité', 'général'];

const EMPTY = {
  enabled: true, cronInterval: '0 */4 * * *',
  stockDaysThreshold: 3, traceabilityHoursThreshold: 24,
  maintenanceDaysThreshold: 7, hrContractDaysThreshold: 30,
  budgetVariancePercent: 20, taskOverdueDaysThreshold: 3,
  dataQualityEnabled: true,
  llmProvider: 'claude', llmModel: '', llmApiKey: '', llmBaseUrl: '',
  agentTone: 'professionnel', agentContext: '', agentRestrictions: '',
};

const VegaAgentConfig = () => {
  const [form, setForm] = useState(EMPTY);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [lastRunInsights, setLastRunInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);

  // Memory
  const [memories, setMemories] = useState([]);
  const [memForm, setMemForm] = useState({ type: 'contexte', category: 'général', content: '' });
  const [addingMem, setAddingMem] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getVegaSettings();
      const s = res.data || {};
      setForm({
        enabled: s.enabled !== false,
        cronInterval: s.cron_interval || '0 */4 * * *',
        stockDaysThreshold: s.stock_days_threshold ?? 3,
        traceabilityHoursThreshold: s.traceability_hours_threshold ?? 24,
        maintenanceDaysThreshold: s.maintenance_days_threshold ?? 7,
        hrContractDaysThreshold: s.hr_contract_days_threshold ?? 30,
        budgetVariancePercent: s.budget_variance_percent ?? 20,
        taskOverdueDaysThreshold: s.task_overdue_days_threshold ?? 3,
        dataQualityEnabled: s.data_quality_enabled !== false,
        llmProvider: s.llm_provider || 'claude',
        llmModel: s.llm_model || '',
        llmApiKey: '',
        llmBaseUrl: s.llm_base_url || '',
        agentTone: s.agent_tone || 'professionnel',
        agentContext: s.agent_context || '',
        agentRestrictions: s.agent_restrictions || '',
      });
      setHasApiKey(s.hasApiKey || false);
      setLastRun(s.last_run_at);
      setLastRunInsights(s.last_run_insights);
    } catch (e) {
      if (e.response?.status !== 404) setError('Erreur de chargement');
    } finally { setLoading(false); }
  }, []);

  const fetchMemories = useCallback(async () => {
    try {
      const res = await getVegaMemories();
      setMemories(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchSettings(); fetchMemories(); }, [fetchSettings, fetchMemories]);

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      await updateVegaSettings({
        enabled: form.enabled,
        cronInterval: form.cronInterval,
        stockDaysThreshold: parseInt(form.stockDaysThreshold),
        traceabilityHoursThreshold: parseInt(form.traceabilityHoursThreshold),
        maintenanceDaysThreshold: parseInt(form.maintenanceDaysThreshold),
        hrContractDaysThreshold: parseInt(form.hrContractDaysThreshold),
        budgetVariancePercent: parseInt(form.budgetVariancePercent),
        taskOverdueDaysThreshold: parseInt(form.taskOverdueDaysThreshold),
        dataQualityEnabled: form.dataQualityEnabled,
        llmProvider: form.llmProvider,
        llmModel: form.llmModel || null,
        llmApiKey: form.llmApiKey || null,
        llmBaseUrl: form.llmBaseUrl || null,
        agentTone: form.agentTone || null,
        agentContext: form.agentContext || null,
        agentRestrictions: form.agentRestrictions || null,
      });
      setSaved(true);
      if (form.llmApiKey) setHasApiKey(true);
      setForm(f => ({ ...f, llmApiKey: '' }));
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(e.response?.data?.error || 'Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleRun = async () => {
    setRunning(true); setRunResult(null);
    try {
      const res = await runVegaAnalysis();
      setRunResult(res.data);
      setLastRun(new Date().toISOString());
      setLastRunInsights(res.data?.insightsCount || 0);
    } catch (e) {
      const msg = e.code === 'ECONNABORTED'
        ? 'Timeout — l\'analyse prend trop de temps. Elle continue en arrière-plan.'
        : e.response?.data?.error || 'Erreur lors de l\'analyse';
      setRunResult({ error: true, message: msg });
    }
    setRunning(false);
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await testVegaConnection();
      setTestResult(res.data);
    } catch { setTestResult({ success: false, message: 'Erreur de connexion' }); }
    setTesting(false);
  };

  const handleAddMemory = async () => {
    if (!memForm.content.trim()) return;
    setAddingMem(true);
    try {
      await addVegaMemory(memForm);
      setMemForm({ type: 'contexte', category: 'général', content: '' });
      fetchMemories();
    } catch { /* ignore */ }
    setAddingMem(false);
  };

  const handleDeleteMemory = async (id) => {
    try { await deleteVegaMemory(id); fetchMemories(); } catch { /* ignore */ }
  };

  const currentProvider = PROVIDERS.find(p => p.value === form.llmProvider);
  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400';
  const textareaCls = inputCls + ' resize-none';

  if (loading) {
    return <div className="flex justify-center py-12"><Loader className="animate-spin text-emerald-500" size={24} /></div>;
  }

  return (
    <div className="space-y-6">

      {/* ── Statut CRON + Contrôles ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock size={16} className="text-emerald-500" />
            VEGA — Statut & Contrôle
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Toggle enabled */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))}
                className={`relative w-11 h-6 rounded-full transition-colors ${form.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.enabled ? 'translate-x-5' : ''}`} />
              </button>
              <span className="text-sm font-medium text-gray-700">
                {form.enabled ? 'CRON actif' : 'CRON désactivé'}
              </span>
            </div>

            {/* Last run info */}
            <div className="flex-1 text-xs text-gray-500">
              {lastRun ? (
                <span>Dernière analyse : <strong>{new Date(lastRun).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}</strong> — {lastRunInsights ?? '?'} insight(s)</span>
              ) : (
                <span className="text-amber-600">Aucune analyse effectuée</span>
              )}
            </div>

            {/* Run now */}
            <button
              onClick={handleRun}
              disabled={running}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition"
            >
              {running ? <Loader size={13} className="animate-spin" /> : <Play size={13} />}
              Lancer maintenant
            </button>
          </div>

          {runResult && !runResult.error && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700">
              Analyse terminée : <strong>{runResult.insightsCount || 0}</strong> insight(s) générés.
            </div>
          )}
          {runResult?.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
              {runResult.message || 'Erreur lors de l\'analyse.'}
            </div>
          )}

          {/* CRON interval */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fréquence du CRON</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CRON_PRESETS.map(p => (
                <button key={p.value} onClick={() => setForm(f => ({ ...f, cronInterval: p.value }))}
                  className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    form.cronInterval === p.value
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1.5">Expression CRON : <code className="bg-gray-100 px-1.5 py-0.5 rounded">{form.cronInterval}</code></p>
          </div>
        </CardContent>
      </Card>

      {/* ── Seuils analyseurs ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle size={16} className="text-emerald-500" />
            VEGA — Seuils d'alerte
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="bg-emerald-50 rounded-lg px-4 py-3 text-sm text-emerald-700 border border-emerald-100">
            VEGA génère des alertes quand ces seuils sont dépassés. Ajustez selon votre tolérance au risque.
          </div>
          {THRESHOLDS.map(t => (
            <div key={t.key} className="flex items-center gap-3">
              <label className="flex-1 text-sm text-gray-700">{t.label}</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number" min={t.min} max={t.max}
                  className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  value={form[t.key]}
                  onChange={e => setForm(f => ({ ...f, [t.key]: e.target.value }))}
                />
                <span className="text-xs text-gray-400 w-12">{t.unit}</span>
              </div>
            </div>
          ))}
          <div className="border-t border-gray-100 pt-3 mt-1">
            <div className="flex items-center gap-3">
              <label className="flex-1 text-sm text-gray-700">Qualité des données — audit cohérence inter-modules</label>
              <button
                onClick={() => setForm(f => ({ ...f, dataQualityEnabled: !f.dataQualityEnabled }))}
                className={`relative w-11 h-6 rounded-full transition-colors ${form.dataQualityEnabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.dataQualityEnabled ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Détecte les produits sans fournisseur, employés sans salaire, budgets manquants, modules vides, etc.</p>
          </div>
        </CardContent>
      </Card>

      {/* ── Config LLM ────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap size={16} className="text-emerald-500" />
            VEGA — Configuration LLM
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-emerald-50 rounded-lg px-4 py-3 text-sm text-emerald-700 border border-emerald-100">
            Le LLM est utilisé pour enrichir les insights (reformulation + recommandations) et pour le chat conversationnel.
            Sans LLM, VEGA génère des alertes brutes sans enrichissement.
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Provider LLM</label>
            <div className="grid grid-cols-2 gap-2">
              {PROVIDERS.map(p => (
                <button key={p.value} onClick={() => setForm(f => ({ ...f, llmProvider: p.value, llmModel: '' }))}
                  className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    form.llmProvider === p.value
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
            <input className={inputCls} value={form.llmModel}
              onChange={e => setForm(f => ({ ...f, llmModel: e.target.value }))}
              placeholder={`Ex: ${currentProvider?.placeholder || 'nom-du-modèle'}`} />
          </div>

          {form.llmProvider !== 'ollama' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Clé API {hasApiKey && <span className="text-xs font-normal text-green-600 ml-1">configurée</span>}
              </label>
              <input type="password" className={inputCls} value={form.llmApiKey}
                onChange={e => setForm(f => ({ ...f, llmApiKey: e.target.value }))}
                placeholder={hasApiKey ? 'Laisser vide pour conserver la clé existante' : 'sk-...'} />
            </div>
          )}

          {form.llmProvider === 'ollama' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL Ollama</label>
              <input className={inputCls} value={form.llmBaseUrl}
                onChange={e => setForm(f => ({ ...f, llmBaseUrl: e.target.value }))}
                placeholder="http://ollama:11434" />
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button onClick={handleTest} disabled={testing}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
              {testing ? <Loader size={13} className="animate-spin" /> : <Zap size={13} />}
              Tester la connexion
            </button>
            {testResult && (
              <span className={`text-xs ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {testResult.success ? '✓' : '✗'} {testResult.message}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Personnalisation ───────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings size={16} className="text-emerald-500" />
            VEGA — Personnalisation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ton de réponse</label>
            <div className="grid grid-cols-2 gap-2">
              {TONES.map(t => (
                <button key={t.value} onClick={() => setForm(f => ({ ...f, agentTone: t.value }))}
                  className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                    form.agentTone === t.value
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}>
                  <div className="font-medium">{t.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contexte personnalisé <span className="text-xs font-normal text-gray-400">— informations sur votre organisation</span>
            </label>
            <textarea rows={4} className={textareaCls} value={form.agentContext}
              onChange={e => setForm(f => ({ ...f, agentContext: e.target.value }))}
              placeholder="Ex: Nous gérons 2 micro-crèches (Bibou, Choupi). Budget annuel ~200k€. Priorité sur la traçabilité alimentaire."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Restrictions <span className="text-xs font-normal text-gray-400">— ce que VEGA ne doit pas faire</span>
            </label>
            <textarea rows={3} className={textareaCls} value={form.agentRestrictions}
              onChange={e => setForm(f => ({ ...f, agentRestrictions: e.target.value }))}
              placeholder="Ex: Ne jamais donner de conseil juridique ferme. Toujours préciser que les recommandations sont indicatives."
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Mémoire ───────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Radar size={16} className="text-emerald-500" />
            VEGA — Mémoire persistante
            <span className="text-xs font-normal text-gray-400 ml-auto">{memories.length} entrée(s)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-emerald-50 rounded-lg px-4 py-3 text-sm text-emerald-700 border border-emerald-100">
            La mémoire est injectée dans chaque conversation. VEGA enrichit aussi sa mémoire automatiquement via les analyses CRON.
          </div>

          {/* Add memory form */}
          <div className="border border-gray-200 rounded-lg p-3 space-y-2">
            <div className="flex gap-2">
              <select value={memForm.type} onChange={e => setMemForm(f => ({ ...f, type: e.target.value }))}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                {MEMORY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={memForm.category} onChange={e => setMemForm(f => ({ ...f, category: e.target.value }))}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                {MEMORY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <input className={inputCls} value={memForm.content}
                onChange={e => setMemForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Ex: Les commandes fournisseurs se font le lundi matin"
                onKeyDown={e => e.key === 'Enter' && handleAddMemory()} />
              <button onClick={handleAddMemory} disabled={addingMem || !memForm.content.trim()}
                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 flex-shrink-0">
                {addingMem ? <Loader size={13} className="animate-spin" /> : <Plus size={13} />}
                Ajouter
              </button>
            </div>
          </div>

          {/* Memories list */}
          {memories.length > 0 && (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {memories.map(m => (
                <div key={m.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg group">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium">{m.type}</span>
                      <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">{m.category}</span>
                      <span className="text-xs text-gray-400">{m.source}</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-snug">{m.content}</p>
                  </div>
                  <button onClick={() => handleDeleteMemory(m.id)}
                    className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition disabled:opacity-50"
          style={{ backgroundColor: '#10b981' }}>
          {saving ? <Loader size={15} className="animate-spin" /> : saved ? <Check size={15} /> : <Save size={15} />}
          {saved ? 'Enregistré' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
};

export default VegaAgentConfig;
